import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { ftIn } from '../utils/formatMeasure';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

const Chevron = ({ open }) => (
  <svg
    width="16" height="16" viewBox="0 0 16 16"
    fill="none" xmlns="http://www.w3.org/2000/svg"
    style={{ transform: open ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.22s ease', flexShrink: 0 }}
  >
    <path d="M6 3L11 8L6 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function Results() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { results, projectName, fileName, form, isEditing, isViewing, projectId } = state || {};
  const [openSections, setOpenSections] = useState({ structural: true, mansardRoof: true, screen: true, insulatedRoof: true, doors: false, kickplate: false, designLoads: true });
  const [saved, setSaved] = useState(false);
  const [spanTableEdits, setSpanTableEdits] = useState({
    windSpeed:        results?.spanTable?.windSpeed        || '',
    exposure:         results?.spanTable?.exposure         || '',
    primaryMembers:   results?.spanTable?.primaryMembers   || '',
    secondaryMembers: results?.spanTable?.secondaryMembers || '',
    screenRoof:       results?.spanTable?.screenRoof       || '',
    solidRoof:        results?.spanTable?.solidRoof        || '',
    deadLoad:         results?.spanTable?.deadLoad         || '',
  });

  const aiSpanTable = results?.spanTable || {};
  const handleSpanChange = (key, value) => {
    setSpanTableEdits(prev => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  const toggleSection = (key) => setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));

  const handleSave = async () => {
    const spanTableToSave = Object.fromEntries(
      Object.entries(spanTableEdits).map(([k, v]) => [k, v.trim() || null])
    );
    const payload = {
      projectName,
      structureType: form?.structureType,
      fileName,
      results: { ...results, _form: form, spanTable: spanTableToSave }
    };
    try {
      if (isEditing && projectId) {
        await axios.put(`/api/projects/${projectId}`, payload);
        setSaved(true);
        alert('Project updated successfully.');
      } else {
        await axios.post('/api/projects', payload);
        setSaved(true);
        alert('Project saved successfully.');
      }
    } catch (err) {
      alert(isEditing ? 'Error updating project.' : 'Error saving project.');
    }
  };

  const handleExportPDF = () => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 40;
    const date = new Date().toLocaleDateString();

    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('PATIO DESIGNS FL', margin, 52);

    doc.setFontSize(13);
    doc.setFont('helvetica', 'normal');
    doc.text('Material List', margin, 70);

    doc.setFontSize(10);
    doc.text(`${projectName}  ·  ${form?.structureType || ''}  ·  ${date}`, margin, 86);

    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.5);
    doc.line(margin, 94, pageWidth - margin, 94);

    let startY = 108;

    const addPdfSection = (title, head, body) => {
      if (startY + 30 > pageHeight - margin) {
        doc.addPage();
        startY = margin;
      }
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(26, 115, 232);
      doc.text(title.toUpperCase(), margin, startY);
      doc.setTextColor(0, 0, 0);
      startY += 6;
      autoTable(doc, {
        head: [head],
        body,
        startY,
        margin: { left: margin, right: margin },
        styles: { fontSize: 8, cellPadding: 3, overflow: 'linebreak' },
        headStyles: { fillColor: [26, 115, 232], textColor: 255, fontStyle: 'bold', fontSize: 8 },
        alternateRowStyles: { fillColor: [245, 247, 250] },
      });
      startY = doc.lastAutoTable.finalY + 18;
    };

    if (results.structuralProfiles) {
      const structuralRows = results.structuralProfiles.map(p => [
        p.element, p.profile, p.cutType === 'diagonal' ? 'Diagonal' : 'Straight',
        p.qty, ftIn(p.totalLength), `${p.piecesNeeded}x ${ftIn(p.commercialSize)}`, ftIn(p.waste)
      ]);
      if (results.columnInserts) {
        structuralRows.push(['Column Inserts', results.columnInserts.insertSize, '—', results.columnInserts.qty, '—', '—', '—']);
      }
      addPdfSection(
        'Structural Profiles',
        ['Element', 'Profile', 'Cut Type', 'Qty', 'Total Length', 'Commercial Size', 'Waste'],
        structuralRows
      );
    }

    if (results.mansardRoof) {
      addPdfSection(
        'Mansard Roof',
        ['Element', 'Profile', 'Qty', 'Total Length', 'Commercial Size', 'Pieces/Bar', 'Waste'],
        [results.mansardRoof.carrierBeams, results.mansardRoof.purlins, results.mansardRoof.rafters].map(m => [
          m.element, m.profile, m.qty, ftIn(m.totalLength),
          `${m.piecesNeeded}x ${ftIn(m.commercialSize)}`, m.piecesPerBar ?? '—', ftIn(m.waste)
        ])
      );
    }

    if (results.insulatedRoof) {
      const ir = results.insulatedRoof;
      addPdfSection(
        'Insulated Roof',
        ['Item', 'Detail', 'Qty / Pieces', 'Commercial Size', 'Waste'],
        [
          ['Fan Beam', `${ir.panels.fanBeamPanels} panels — ${ftIn(ir.panels.panelLength)} each`,
            `${ir.panels.qty} total`, 'Custom length', `+${ir.panels.wastePercent}%`],
          ['Step Valance / Fascia', `${ftIn(ir.stepBalance.totalLength)} run`,
            `${ir.stepBalance.pieces} pcs`, ir.stepBalance.commercialSize, ftIn(ir.stepBalance.waste)],
          ['Header (3in Box)', `${ftIn(ir.header.totalLength)} run`,
            `${ir.header.pieces} pcs`, ir.header.commercialSize, ftIn(ir.header.waste)],
          [`E-Gutter (${ir.egutter.size})`, `${ftIn(ir.egutter.totalLength)} run`,
            `${ir.egutter.pieces} pcs`, ir.egutter.commercialSize, ftIn(ir.egutter.waste)],
        ]
      );
    }

    if (results.screen) {
      const sc = results.screen;
      const screenRows = [
        [`Roof — Long (${ftIn(sc.roof.rafterSpacing)} spacing)`, `${sc.roof.rollWidth}ft`,
          `${sc.roof.longStrips} x ${ftIn(sc.roof.longLength)}`, ftIn(sc.roof.totalLengthLong), sc.roof.rollsNeeded],
      ];
      if (sc.roof.shortStrips > 0) {
        screenRows.push(['Roof — Short', `${sc.roof.rollWidth}ft`,
          `${sc.roof.shortStrips} x ${ftIn(sc.roof.shortLength)}`, ftIn(sc.roof.totalLengthShort),
          Math.ceil((sc.roof.totalLengthShort || 0) / 100)]);
      }
      screenRows.push(
        ['Front Wall', `${sc.frontWall.rollWidth}ft`, `Height: ${sc.frontWall.heightInches}"`,
          ftIn(sc.frontWall.totalLength), sc.frontWall.rollsNeeded],
        ['Lateral Walls (x2)', `${sc.lateralWalls.rollWidth}ft`,
          `${sc.lateralWalls.heightInches - sc.lateralWalls.slopeAdjustInches}" + ${sc.lateralWalls.slopeAdjustInches}" slope`,
          ftIn(sc.lateralWalls.totalLength), sc.lateralWalls.rollsNeeded],
      );
      addPdfSection('Screen', ['Zone', 'Roll Width', 'Strips x Length', 'Total Length (+12%)', 'Rolls'], screenRows);
    }

    if (results.doors) {
      addPdfSection(
        'Doors & Hardware',
        ['Door Size', 'Qty', 'Z-Bars', 'Door Kits', 'Back Sweeps', 'Hinges', 'Latches'],
        results.doors.doors.map(d => [`${d.doorSize}"`, d.qty, d.zBar, d.doorKit, d.backSweep, d.hinges, d.latch])
      );
    }

    if (results.kickPlate) {
      const kp = results.kickPlate;
      addPdfSection(
        'Kick Plate',
        ['Size', 'Total Length', 'Pieces', 'Commercial Size', 'Waste'],
        [[kp.kickPlateSize, ftIn(kp.totalLength), kp.extrudedKickPlate.pieces,
          kp.extrudedKickPlate.commercialSize, ftIn(kp.extrudedKickPlate.waste)]]
      );
    }

    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(150, 150, 150);
      doc.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 20, { align: 'center' });
    }

    doc.save(`${projectName}_materials.pdf`);
  };

  const handleExportExcel = () => {
    const rows = [];
    const date = new Date().toLocaleDateString();

    rows.push(['PATIO DESIGNS FL']);
    rows.push([`${projectName} — ${form?.structureType || ''}`]);
    rows.push([date]);
    rows.push([]);

    const addXlsSection = (title, headers, dataRows) => {
      rows.push([title]);
      rows.push(headers);
      dataRows.forEach(r => rows.push(r));
      rows.push([]);
    };

    if (results.structuralProfiles) {
      const structuralRows = results.structuralProfiles.map(p => [
        p.element, p.profile, p.cutType === 'diagonal' ? 'Diagonal' : 'Straight',
        p.qty, ftIn(p.totalLength), `${p.piecesNeeded}x ${ftIn(p.commercialSize)}`, ftIn(p.waste)
      ]);
      if (results.columnInserts) {
        structuralRows.push(['Column Inserts', results.columnInserts.insertSize, '—', results.columnInserts.qty, '—', '—', '—']);
      }
      addXlsSection(
        'STRUCTURAL PROFILES',
        ['Element', 'Profile', 'Cut Type', 'Qty', 'Total Length', 'Commercial Size', 'Waste'],
        structuralRows
      );
    }

    if (results.mansardRoof) {
      addXlsSection(
        'MANSARD ROOF',
        ['Element', 'Profile', 'Qty', 'Total Length', 'Commercial Size', 'Pieces/Bar', 'Waste'],
        [results.mansardRoof.carrierBeams, results.mansardRoof.purlins, results.mansardRoof.rafters].map(m => [
          m.element, m.profile, m.qty, ftIn(m.totalLength),
          `${m.piecesNeeded}x ${ftIn(m.commercialSize)}`, m.piecesPerBar ?? '—', ftIn(m.waste)
        ])
      );
    }

    if (results.insulatedRoof) {
      const ir = results.insulatedRoof;
      addXlsSection(
        'INSULATED ROOF',
        ['Item', 'Detail', 'Qty / Pieces', 'Commercial Size', 'Waste'],
        [
          ['Fan Beam', `${ir.panels.fanBeamPanels} panels — ${ftIn(ir.panels.panelLength)} each`,
            `${ir.panels.qty} total`, 'Custom length', `+${ir.panels.wastePercent}%`],
          ['Step Valance / Fascia', `${ftIn(ir.stepBalance.totalLength)} run`,
            `${ir.stepBalance.pieces} pcs`, ir.stepBalance.commercialSize, ftIn(ir.stepBalance.waste)],
          ['Header (3in Box)', `${ftIn(ir.header.totalLength)} run`,
            `${ir.header.pieces} pcs`, ir.header.commercialSize, ftIn(ir.header.waste)],
          [`E-Gutter (${ir.egutter.size})`, `${ftIn(ir.egutter.totalLength)} run`,
            `${ir.egutter.pieces} pcs`, ir.egutter.commercialSize, ftIn(ir.egutter.waste)],
        ]
      );
    }

    if (results.screen) {
      const sc = results.screen;
      const screenRows = [
        [`Roof Long (${ftIn(sc.roof.rafterSpacing)} spacing)`, `${sc.roof.rollWidth}ft`,
          `${sc.roof.longStrips} x ${ftIn(sc.roof.longLength)}`, ftIn(sc.roof.totalLengthLong), sc.roof.rollsNeeded],
      ];
      if (sc.roof.shortStrips > 0) {
        screenRows.push(['Roof Short', `${sc.roof.rollWidth}ft`,
          `${sc.roof.shortStrips} x ${ftIn(sc.roof.shortLength)}`, ftIn(sc.roof.totalLengthShort),
          Math.ceil((sc.roof.totalLengthShort || 0) / 100)]);
      }
      screenRows.push(
        ['Front Wall', `${sc.frontWall.rollWidth}ft`, `Height: ${sc.frontWall.heightInches}"`,
          ftIn(sc.frontWall.totalLength), sc.frontWall.rollsNeeded],
        ['Lateral Walls (x2)', `${sc.lateralWalls.rollWidth}ft`,
          `${sc.lateralWalls.heightInches - sc.lateralWalls.slopeAdjustInches}" + ${sc.lateralWalls.slopeAdjustInches}" slope`,
          ftIn(sc.lateralWalls.totalLength), sc.lateralWalls.rollsNeeded],
      );
      addXlsSection('SCREEN', ['Zone', 'Roll Width', 'Strips x Length', 'Total Length (+12%)', 'Rolls'], screenRows);
    }

    if (results.doors) {
      addXlsSection(
        'DOORS & HARDWARE',
        ['Door Size', 'Qty', 'Z-Bars', 'Door Kits', 'Back Sweeps', 'Hinges', 'Latches'],
        results.doors.doors.map(d => [`${d.doorSize}"`, d.qty, d.zBar, d.doorKit, d.backSweep, d.hinges, d.latch])
      );
    }

    if (results.kickPlate) {
      const kp = results.kickPlate;
      addXlsSection(
        'KICK PLATE',
        ['Size', 'Total Length', 'Pieces', 'Commercial Size', 'Waste'],
        [[kp.kickPlateSize, ftIn(kp.totalLength), kp.extrudedKickPlate.pieces,
          kp.extrudedKickPlate.commercialSize, ftIn(kp.extrudedKickPlate.waste)]]
      );
    }

    rows.push(['SUMMARY']);
    if (results.summary?.totalProfilePieces) rows.push(['Total profile pieces', results.summary.totalProfilePieces]);
    if (results.summary?.totalScreenRolls) rows.push(['Total screen rolls', results.summary.totalScreenRolls]);
    if (results.summary?.totalPanels) rows.push(['Total panels', results.summary.totalPanels]);

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(rows);
    const colWidths = rows.reduce((acc, row) => {
      row.forEach((cell, i) => {
        const len = String(cell ?? '').length;
        acc[i] = Math.max(acc[i] || 10, len + 2);
      });
      return acc;
    }, {});
    ws['!cols'] = Object.values(colWidths).map(w => ({ wch: Math.min(w, 40) }));

    XLSX.utils.book_append_sheet(wb, ws, 'Material List');
    XLSX.writeFile(wb, `${projectName}_materials.xlsx`);
  };

  function wasteColor(wasteAmount, totalMaterial) {
    if (wasteAmount === null || wasteAmount === undefined || !totalMaterial) return '#34a853';
    const pct = (wasteAmount / totalMaterial) * 100;
    if (pct <= 10) return '#34a853';
    if (pct <= 25) return '#f9ab00';
    return '#ea4335';
  }

  if (!results) return <div>No results found. <a href="/">Go back</a></div>;

  return (
    <div>
      <div className="page-header">
        <div className="breadcrumb"><a href="/">Home</a> / {projectName} / <strong>Results</strong></div>
        <h1 className="page-title">Material List</h1>
        <p className="page-subtitle">{projectName} — {form?.structureType}</p>
      </div>

      {isEditing && (
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#fff3e0', border: '1px solid #fb8c00', color: '#e65100', borderRadius: '6px', padding: '6px 12px', fontSize: '13px', fontWeight: '500', marginBottom: '16px' }}>
          ✏️ Editing: {projectName}
        </div>
      )}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
        {isViewing ? (
          <button className="btn-secondary" onClick={() => navigate('/form', {
            state: {
              isEditing: true,
              projectId,
              projectName,
              fileName,
              form: results._form,
              extractedData: results._form,
              fromResults: true
            }
          })}>✏️ Edit</button>
        ) : isEditing ? (
          <button className="btn-primary" onClick={handleSave} disabled={saved}>
            {saved ? '✓ Updated' : '💾 Update Project'}
          </button>
        ) : (
          <button className="btn-primary" onClick={handleSave} disabled={saved}>
            {saved ? '✓ Saved' : '💾 Save Project'}
          </button>
        )}
        <button className="btn-secondary" onClick={handleExportPDF}>📄 Export PDF</button>
        <button className="btn-secondary" onClick={handleExportExcel}>📊 Export Excel</button>
      </div>

      {/* Structural Profiles */}
      <div className="section-header" onClick={() => toggleSection('structural')}>
        Structural Profiles <Chevron open={openSections.structural} />
      </div>
      {openSections.structural && results.structuralProfiles && (
        <div className="card" style={{ marginBottom: '16px' }}>
          <table className="material-table">
            <thead>
              <tr><th>Element</th><th>Profile</th><th>Cut Type</th><th>Qty</th><th>Total Length</th><th>Commercial Size</th><th>Waste</th></tr>
            </thead>
            <tbody>
              {results.structuralProfiles.map((p, i) => (
                <React.Fragment key={i}>
                  <tr>
                    <td>{p.element}</td>
                    <td>{p.profile}</td>
                    <td>{p.cutType === 'diagonal' ? 'Diagonal' : 'Straight'}</td>
                    <td>{p.qty}</td>
                    <td>{ftIn(p.totalLength)}</td>
                    <td>{p.piecesNeeded}× {ftIn(p.commercialSize)}</td>
                    <td><span style={{ color: wasteColor(p.waste, p.commercialSize * p.piecesNeeded) }}>● {ftIn(p.waste)}</span></td>
                  </tr>
                  {p.piecesPerBar && (
                    <tr>
                      <td colSpan={7} style={{ fontSize: '12px', color: '#666', paddingTop: '2px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0' }}>
                        ↳ {p.piecesNeeded} bar{p.piecesNeeded !== 1 ? 's' : ''} de {ftIn(p.commercialSize)} → {p.piecesPerBar} corte{p.piecesPerBar !== 1 ? 's' : ''} por barra
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {results.columnInserts && (
                <tr>
                  <td>Column Inserts</td>
                  <td>{results.columnInserts.insertSize}</td>
                  <td>—</td>
                  <td>{results.columnInserts.qty}</td>
                  <td>—</td>
                  <td>—</td>
                  <td>—</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Mansard Roof */}
      {results.mansardRoof && (
        <>
          <div className="section-header" onClick={() => toggleSection('mansardRoof')}>
            Mansard Roof <Chevron open={openSections.mansardRoof} />
          </div>
          {openSections.mansardRoof && (
            <div className="card" style={{ marginBottom: '16px' }}>
              <p style={{ fontSize: '13px', color: '#666', marginBottom: '12px', fontWeight: '500' }}>Structural Members</p>
              <table className="material-table" style={{ marginBottom: '20px' }}>
                <thead>
                  <tr><th>Element</th><th>Profile</th><th>Qty</th><th>Total Length</th><th>Commercial Size</th><th>Pieces/Bar</th><th>Waste</th></tr>
                </thead>
                <tbody>
                  {[results.mansardRoof.carrierBeams, results.mansardRoof.purlins, results.mansardRoof.rafters].map((m, i) => (
                    <tr key={i}>
                      <td>{m.element}</td>
                      <td>{m.profile}</td>
                      <td>{m.qty}</td>
                      <td>{ftIn(m.totalLength)}</td>
                      <td>{m.piecesNeeded}× {ftIn(m.commercialSize)}</td>
                      <td>{m.piecesPerBar ?? '—'}</td>
                      <td><span style={{ color: wasteColor(m.waste, m.commercialSize * m.piecesNeeded) }}>● {ftIn(m.waste)}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p style={{ fontSize: '13px', color: '#666', marginBottom: '12px', fontWeight: '500' }}>Screen — Mansard Walls</p>
              <table className="material-table">
                <thead>
                  <tr><th>Zone</th><th>Roll Width</th><th>Rolls Needed</th><th>Coverage</th><th>Waste</th></tr>
                </thead>
                <tbody>
                  {results.mansardRoof.screen.rollResults.map((r, i) => (
                    <tr key={i}>
                      <td>Mansard walls</td>
                      <td>{ftIn(r.rollWidth)}</td>
                      <td>{r.rollsNeeded} rolls</td>
                      <td>{ftIn(r.totalLengthNeeded)}</td>
                      <td>3%</td>
                    </tr>
                  ))}
                  <tr>
                    <td>Spline</td><td>flat</td>
                    <td>{results.mansardRoof.screen.spline.rollsNeeded} rolls of {results.mansardRoof.screen.spline.rollSize}</td>
                    <td>{ftIn(results.mansardRoof.screen.spline.totalFeet)} total</td>
                    <td>3%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Insulated Roof */}
      {results.insulatedRoof && (
        <>
          <div className="section-header" onClick={() => toggleSection('insulatedRoof')}>
            Insulated Roof <Chevron open={openSections.insulatedRoof} />
          </div>
          {openSections.insulatedRoof && (
            <div className="card" style={{ marginBottom: '16px' }}>
              <table className="material-table">
                <thead>
                  <tr><th>Item</th><th>Detail</th><th>Qty / Pieces</th><th>Commercial Size</th><th>Waste</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Fan Beam</td>
                    <td>{results.insulatedRoof.panels.fanBeamPanels} panels — {ftIn(results.insulatedRoof.panels.panelLength)} each</td>
                    <td>
                      {results.insulatedRoof.panels.qty} total ({results.insulatedRoof.panels.standardPanels} std +{' '}
                      {results.insulatedRoof.panels.fanBeamPanels} fan beam)
                    </td>
                    <td>Custom length</td>
                    <td>+{results.insulatedRoof.panels.wastePercent}%</td>
                  </tr>
                  <tr>
                    <td>Step Valance / Fascia</td>
                    <td>{ftIn(results.insulatedRoof.stepBalance.totalLength)} run</td>
                    <td>{results.insulatedRoof.stepBalance.pieces} pieces</td>
                    <td>{results.insulatedRoof.stepBalance.commercialSize}</td>
                    <td><span style={{ color: wasteColor(results.insulatedRoof.stepBalance.waste, parseFloat(results.insulatedRoof.stepBalance.commercialSize) * results.insulatedRoof.stepBalance.pieces) }}>● {ftIn(results.insulatedRoof.stepBalance.waste)}</span></td>
                  </tr>
                  <tr>
                    <td>Header (3in Box)</td>
                    <td>{ftIn(results.insulatedRoof.header.totalLength)} run</td>
                    <td>{results.insulatedRoof.header.pieces} pieces</td>
                    <td>{results.insulatedRoof.header.commercialSize}</td>
                    <td><span style={{ color: wasteColor(results.insulatedRoof.header.waste, parseFloat(results.insulatedRoof.header.commercialSize) * results.insulatedRoof.header.pieces) }}>● {ftIn(results.insulatedRoof.header.waste)}</span></td>
                  </tr>
                  <tr>
                    <td>E-Gutter ({results.insulatedRoof.egutter.size})</td>
                    <td>
                      {ftIn(results.insulatedRoof.egutter.totalLength)} run +{' '}
                      L cap ×{results.insulatedRoof.egutter.leftCap},{' '}
                      R cap ×{results.insulatedRoof.egutter.rightCap}
                    </td>
                    <td>{results.insulatedRoof.egutter.pieces} pieces</td>
                    <td>{results.insulatedRoof.egutter.commercialSize}</td>
                    <td><span style={{ color: wasteColor(results.insulatedRoof.egutter.waste, parseFloat(results.insulatedRoof.egutter.commercialSize) * results.insulatedRoof.egutter.pieces) }}>● {ftIn(results.insulatedRoof.egutter.waste)}</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Screen */}
      {results.screen && (
        <>
          <div className="section-header" onClick={() => toggleSection('screen')}>
            Screen <Chevron open={openSections.screen} />
          </div>
          {openSections.screen && (
            <div className="card" style={{ marginBottom: '16px' }}>
              <div className="screen-zone">
                <p style={{ fontSize: '12px', fontWeight: '600', color: '#444', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Roof</p>
                <p style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                  Rafter spacing: {ftIn(results.screen.roof.rafterSpacing)} → Roll width: {results.screen.roof.rollWidth}ft
                </p>
                <table className="material-table">
                  <thead>
                    <tr><th>Zone</th><th>Strips × Length</th><th>Total Length (+12%)</th><th>Rolls</th></tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Long strips</td>
                      <td>{results.screen.roof.longStrips} × {ftIn(results.screen.roof.longLength)}</td>
                      <td>{ftIn(results.screen.roof.totalLengthLong)}</td>
                      <td>{Math.ceil((results.screen.roof.totalLengthLong || 0) / 100)}</td>
                    </tr>
                    {results.screen.roof.shortStrips > 0 && (
                      <tr>
                        <td>Short strips</td>
                        <td>{results.screen.roof.shortStrips} × {ftIn(results.screen.roof.shortLength)}</td>
                        <td>{ftIn(results.screen.roof.totalLengthShort)}</td>
                        <td>{Math.ceil((results.screen.roof.totalLengthShort || 0) / 100)}</td>
                      </tr>
                    )}
                    <tr style={{ fontWeight: '600' }}>
                      <td colSpan={3}>Total roof rolls</td>
                      <td>{results.screen.roof.rollsNeeded}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="screen-zone">
                <p style={{ fontSize: '12px', fontWeight: '600', color: '#444', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Front Wall</p>
                <table className="material-table">
                  <thead>
                    <tr><th>Roll Width</th><th>Height</th><th>Total Length (+12%)</th><th>Rolls</th></tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{results.screen.frontWall.rollWidth}ft</td>
                      <td>{results.screen.frontWall.heightInches}"</td>
                      <td>{ftIn(results.screen.frontWall.totalLength)}</td>
                      <td>{results.screen.frontWall.rollsNeeded}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="screen-zone">
                <p style={{ fontSize: '12px', fontWeight: '600', color: '#444', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Lateral Walls (×2)</p>
                <table className="material-table">
                  <thead>
                    <tr><th>Roll Width</th><th>Base Height</th><th>Slope Adjust</th><th>Total Length (+12%)</th><th>Rolls</th></tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{results.screen.lateralWalls.rollWidth}ft</td>
                      <td>{results.screen.lateralWalls.heightInches - results.screen.lateralWalls.slopeAdjustInches}"</td>
                      <td>+{results.screen.lateralWalls.slopeAdjustInches}"</td>
                      <td>{ftIn(results.screen.lateralWalls.totalLength)}</td>
                      <td>{results.screen.lateralWalls.rollsNeeded}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Doors */}
      <div className="section-header" onClick={() => toggleSection('doors')}>
        Doors & Hardware <Chevron open={openSections.doors} />
      </div>
      {openSections.doors && results.doors && (
        <div className="card" style={{ marginBottom: '16px' }}>
          <table className="material-table">
            <thead>
              <tr><th>Door Size</th><th>Qty</th><th>Z-Bars</th><th>Door Kits</th><th>Back Sweeps</th><th>Hinges</th><th>Latches</th></tr>
            </thead>
            <tbody>
              {results.doors.doors.map((d, i) => (
                <tr key={i}>
                  <td>{d.doorSize}"</td><td>{d.qty}</td><td>{d.zBar}</td>
                  <td>{d.doorKit}</td><td>{d.backSweep}</td><td>{d.hinges}</td><td>{d.latch}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Kick Plate */}
      <div className="section-header" onClick={() => toggleSection('kickplate')}>
        Kick Plate <Chevron open={openSections.kickplate} />
      </div>
      {openSections.kickplate && results.kickPlate && (
        <div className="card" style={{ marginBottom: '16px' }}>
          <table className="material-table">
            <thead>
              <tr><th>Size</th><th>Total Length</th><th>Extruded Pieces</th><th>Commercial Size</th><th>Waste</th></tr>
            </thead>
            <tbody>
              <tr>
                <td>{results.kickPlate.kickPlateSize}</td>
                <td>{ftIn(results.kickPlate.totalLength)}</td>
                <td>{results.kickPlate.extrudedKickPlate.pieces}</td>
                <td>{results.kickPlate.extrudedKickPlate.commercialSize}</td>
                <td><span style={{ color: wasteColor(results.kickPlate.extrudedKickPlate.waste, parseFloat(results.kickPlate.extrudedKickPlate.commercialSize) * results.kickPlate.extrudedKickPlate.pieces) }}>● {ftIn(results.kickPlate.extrudedKickPlate.waste)}</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Design Loads */}
      <div className="section-header" onClick={() => toggleSection('designLoads')}>
        Design Loads (from plan) <Chevron open={openSections.designLoads} />
      </div>
      {openSections.designLoads && (
        <div className="card" style={{ marginBottom: '16px' }}>
          <table className="material-table">
            <thead>
              <tr><th>Parameter</th><th>Value</th></tr>
            </thead>
            <tbody>
              {[
                ['windSpeed',        'Wind Speed'],
                ['exposure',         'Exposure Category'],
                ['primaryMembers',   'Primary Members Load'],
                ['secondaryMembers', 'Secondary Members Load'],
                ['screenRoof',       'Screen Roof Pressure'],
                ['solidRoof',        'Solid Roof Pressure'],
                ['deadLoad',         'Dead Load'],
              ].map(([key, label]) => (
                <tr key={key}>
                  <td>{label}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        style={{ border: '1px solid #e0e0e0', borderRadius: '4px', padding: '4px 8px', fontSize: '13px', width: '100%', background: 'white' }}
                        value={spanTableEdits[key]}
                        onChange={e => handleSpanChange(key, e.target.value)}
                        placeholder="—"
                      />
                      {aiSpanTable[key] && (
                        <span style={{ color: '#34a853', fontSize: '12px', whiteSpace: 'nowrap' }}>✓ from plan</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Summary */}
      <div className="summary-card">
        {results.summary.totalProfilePieces && <div className="summary-item">Total profiles: {results.summary.totalProfilePieces} pieces</div>}
        {results.summary.totalScreenRolls && <div className="summary-item">Total screen rolls: {results.summary.totalScreenRolls} rolls</div>}
        {results.summary.totalPanels && <div className="summary-item">Total panels: {results.summary.totalPanels}</div>}
      </div>
    </div>
  );
}

export default Results;
